/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse, stringify } from 'qs';
import {
  Table,
  Button,
  Tooltip,
  Input,
  Popconfirm,
  Spin,
  message,
} from 'antd';
import { PlusCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { verifyFeatureAccess } from '../../../global/featureFlags';

const { Search } = Input;

const parseOptions = {
  ignoreQueryPrefix: true,
};

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

class ManageProviders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      emaTaskProviders: null,
      currentPage: 1,
      selectedProvider: '',
      searchValue: '',
      canManageProvider: false,
    };
  }

  componentDidMount = async (page = 1) => {
    const { getTaskProvidersByQuery, getUserInfo } = this.props;
    await getUserInfo();
    const { availableEmrs } = this.props;
    this.setState({ availableEmrs });
    const params = {};
    params.page = page;
    const query = stringify(params, stringifyOptions);
    const canManageProvider = await verifyFeatureAccess('manage_providers');
    await getTaskProvidersByQuery(query);
    this.setState({
      emaTaskProviders: this.props.allEmaTaskProviders.providers,
      canManageProvider,
    });
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      emaTaskProviders: nextProps.allEmaTaskProviders.providers,
    });
  }

  getQueryData = async (page = 1) => {
    const { location, headers, getTaskProvidersByQuery } = this.props;
    const totalPages = (headers ? parseInt(headers.total_count, 10) : 1);
    let params = {};
    if (location.search) {
      params = parse(location.search, parseOptions);
      // eslint-disable-next-line no-param-reassign
      page = parseInt((params.page <= totalPages ? params.page : 1), 10);
    }
    params.page = page;
    const query = stringify(params, stringifyOptions);
    await getTaskProvidersByQuery(query);
    this.setState({
      emaTaskProviders: this.props.allEmaTaskProviders.providers,
    });
  }

  openEditProviderModal = (provider) => {
    const {
      openModal,
      isAdmin,
    } = this.props;
    const { availableEmrs, currentPage, searchValue } = this.state;
    availableEmrs.sort((a, b) => (a.name > b.name ? 1 : -1));
    const modalType = {
      type: 'ADD_TASK_PROVIDER',
      data: {
        provider,
        isAdmin,
        availableEmrs,
        currentPage,
        currentSearch: searchValue,
      },
    };
    return openModal(modalType);
  }

  handleTableChange = (pagination) => {
    const { current: page } = pagination;
    const { searchValue } = this.state;
    this.setState({
      currentPage: page,
    });
    const { getTaskProvidersByQuery: fetchProviders } = this.props;
    const params = {
      page,
    };
    if (searchValue.trim() !== '') {
      params.contains = searchValue;
    }
    const query = stringify(params, stringifyOptions);
    fetchProviders(query);
  };

  searchProviders = (value) => {
    const { paramTools, getTaskProvidersByQuery } = this.props;
    const { params } = paramTools;
    if (params.contains === '' || value === '') {
      delete params.contains;
      params.page = 1;
    } else {
      params.contains = value;
      params.page = 1;
    }
    const query = stringify(params, stringifyOptions);
    this.setState({ searchValue: value, currentPage: 1 });
    const emptySearch = (value.trim() === '');
    if (emptySearch) return getTaskProvidersByQuery('');
    getTaskProvidersByQuery(query);
  }

  deleteTaskProvider = async (provider) => {
    const { deleteTaskProvider } = this.props;
    await deleteTaskProvider(provider.id);
    const { error, errorCode } = this.props;
    if (error && errorCode === 403) {
      message.error('Cannot delete Task Provider. You need permission to perform this action');
    }

    this.getQueryData();
    window.location.reload();
  }

  render() {
    const {
      emaTaskProviders,
      currentPage,
      selectedProvider,
      searchValue,
      canManageProvider,
      availableEmrs,
    } = this.state;
    const isEditing = provider => provider.task_name === selectedProvider;
    const {
      headers,
      isLoading,
      isAdmin,
    } = this.props;
    const totalResults = headers ? headers.total_count : 1;
    const columns = [
      {
        title: (
          <Tooltip title="Must match the name exactly as it appears in your EMR's Assign To dropdown — case, punctuation, spacing and all. If it doesn't match, tasks created in Medsender won't route to anyone in your EMR.">
            Task Provider Name
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: (
          <Tooltip title="User = an individual staff member in your EMR. Staff Group = a named group that multiple people belong to (e.g. 'Front Desk', 'Billing'). Pick the one that matches how the entry is set up in your EMR.">
            Task Provider Type
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'provider_type',
        dataIndex: 'provider_type',
      },
      {
        title: (
          <Tooltip title="Set to Yes only for clinicians who can be the Ordering Provider on a chart (typically MDs, NPs, PAs). Yes entries appear in the Ordering Provider dropdown when sending records to chart. Leave No for front-desk, billing, and other non-clinical staff or groups.">
            Is Provider
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'is_provider',
        dataIndex: 'is_provider',
      },
      {
        title: (
          <Tooltip title="Only Active entries appear in Send to Chart and Create Task modals. Set to Inactive instead of deleting when someone leaves — keeps history intact and stops them from being assigned new work.">
            Status
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'is_active',
        dataIndex: 'is_active',
      },
      {
        title: (
          <Tooltip title="Which EMR login this entry belongs to. Entries tied to a specific EMR Credential only appear when that credential is in use. Entries set to 'Organization' are shared across all credentials and are the fallback when no credential is attached to the fax line.">
            Emr Credential
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'emr_credential_name',
        dataIndex: 'emr_credential_name',
        hidden: availableEmrs ? availableEmrs.length <= 2 : false,
      },
      {
        title: '',
        key: 'edit',
        align: 'left',
        render: (_, provider) => {
          const editable = isEditing(provider);
          return editable ? (
            <span>
              <Button
                // eslint-disable-next-line no-script-url
                href="javascript:;"
                onClick={() => this.save(provider)}
                style={{
                  marginRight: 8,
                }}
              >
                Save
              </Button>
              <Popconfirm title="Cancel edit without saving?" onConfirm={() => this.setState({ selectedProvider: '' })}>
                <a>Cancel</a>
              </Popconfirm>
            </span>
          ) : (
            <span>
              {}
              {
                ((isAdmin && canManageProvider))
                && (
                <Button disabled={selectedProvider !== ''} onClick={() => this.openEditProviderModal(provider)} style={{ marginRight: 10 }}>
                  Edit
                </Button>
                )
              }
              {}
              {
              ((isAdmin))
                && (
                <Popconfirm title="Delete this Provider? (This action is permanent and cannot be reversed. " onConfirm={() => this.deleteTaskProvider(provider)}>
                  <a>Delete</a>
                </Popconfirm>
                )
              }
            </span>
          );
        },
      },
    ].filter(column => !column.hidden);

    if (isLoading) return (<Spin size="large" />);

    return (
      // eslint-disable-next-line object-curly-newline
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '16px 40px 40px', textAlign: 'left' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, marginBottom: 16 }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: '#181818', margin: 0, textAlign: 'left' }}>
            Manage Task Providers
          </h2>
          <p style={{ color: '#5c5c5c', fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: 'left' }}>
            The people and groups in your EMR that tasks can be assigned to.
            <br />
            Add or sync providers here so they show up in the Assign To list on Task
            Templates.
            {' '}
            <Tooltip
              placement="bottom"
              title={(
                <div style={{ maxWidth: 340, lineHeight: 1.5 }}>
                  Task Providers mirror your EMR&apos;s Assign To options (users and staff
                  groups). Templates use them to route auto-created tasks to the right
                  person or group. Keep this list in sync when staff join, leave, or
                  change roles in your EMR.
                </div>
              )}
            >
              <a style={{
                color: '#662c88',
                fontWeight: 500,
                cursor: 'help',
                textDecoration: 'none',
                borderBottom: '1px dotted currentColor',
              }}
              >
                How this works
              </a>
            </Tooltip>
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          {((canManageProvider && isAdmin) || !canManageProvider)
            ? (
              <Button
                icon={<PlusCircleOutlined />}
                type="primary"
                onClick={this.openEditProviderModal}
              >
                Add Task Provider
              </Button>
            )
            : (
              <span />
            )
          }
          <div>
            <Search
              placeholder="search providers"
              value={searchValue}
              onChange={e => this.setState({ searchValue: e.target.value })}
              onSearch={this.searchProviders}
              style={{ width: 200, height: '32px', marginRight: '10px' }}
              allowClear
            />
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <Table
            size="small"
            dataSource={emaTaskProviders}
            columns={columns}
            rowKey={record => record.id}
            bordered
            onChange={this.handleTableChange}
            pagination={{
              total: totalResults,
              current: currentPage,
              showSizeChanger: false,
              showTotal: (total, range) => `Viewing ${range[0]}-${range[1]} of ${total} providers`,
              position: ['bottomCenter'],
            }}
          />
        </div>
      </div>
    );
  }
}

ManageProviders.defaultProps = {
  isLoading: false,
  allEmaTaskProviders: {},
  availableEmrs: [],
  headers: {
    page: 1,
    'per-page': '10',
    total_count: 1,
  },
  paramTools: {
    push: () => {},
    params: {},
    stringify: () => {},
    stringifyOptions: {},
  },
  errorCode: '',
};

ManageProviders.propTypes = {
  isLoading: PropTypes.bool,
  headers: PropTypes.shape({
    page: PropTypes.number,
    'per-page': PropTypes.string,
    total_count: PropTypes.number,
  }),
  paramTools: PropTypes.shape({
    params: PropTypes.object,
    stringify: PropTypes.func,
    stringifyOptions: PropTypes.object,
    push: PropTypes.func,
  }),
  location: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  deleteTaskProvider: PropTypes.func.isRequired,
  getTaskProvidersByQuery: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  allEmaTaskProviders: PropTypes.object,
  error: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  availableEmrs: PropTypes.array,
};

export default connect(state => ({
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  emaTaskProviders: state.auth.data,
  isLoading: state.userProfile.isLoading,
  isAdmin: state.userProfile.isAdmin,
  headers: state.userProfile.headers,
  error: state.userProfile.error,
}))(ManageProviders);
